import { Link } from "react-router-dom";
import { CheckCircle2, Sparkles, ArrowUpRight, Code2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { PageHeader } from "../components/shared/PageHeader";
import { CTASection } from "../components/shared/CTASection";
import { AUDITGUARD, AI_SOLUTIONS, AI_ICONS, IMAGES } from "../data/content";

const LiveBadge = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-600">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
  </span>
);

const LiveBadgeDark = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-300">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Now Live
  </span>
);

const API_CODE = `curl -X POST https://www.globalelectaviation.com/api/ai/risk-scoring \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "organization_name": "SkyLink Charter",
    "organization_type": "Charter Operator",
    "regulatory_framework": "FAA Part 5 SMS",
    "audit_type": "Risk scoring & SPI assessment",
    "current_state": "Describe operations, hazards and controls..."
  }'`;

const API_ENDPOINTS = [
  "/ai/audit-prep", "/ai/gap-analysis", "/ai/document-generator",
  "/ai/corrective-action", "/ai/risk-scoring", "/ai/training-competency", "/ai/safety-analyzer",
];

const API_FEATURES = [
  "All seven AI modules available over simple REST calls",
  "One header to authenticate — X-API-Key with keys from your dashboard",
  "Generate reports, pull history and check quota programmatically",
  "Free tier included — no separate developer plan required",
];

const Flagship = () => (
  <section data-testid="auditguard-section" className="bg-background py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <div className="grain relative overflow-hidden rounded-3xl border border-border bg-navy text-white">
          <img src={IMAGES.ai} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/60" />
          <div className="relative grid gap-10 p-8 lg:grid-cols-12 lg:p-14">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-aero">
                  <Sparkles className="h-3.5 w-3.5" /> Flagship Platform
                </span>
                <LiveBadgeDark />
              </div>
              <div className="mt-6 flex items-center gap-5">
                <span className="inline-flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white shadow-lg ring-1 ring-white/10">
                  <img src={AI_ICONS.auditguard} alt="AuditGuard AI logo mark" className="h-14 w-14 object-contain" />
                </span>
                <div>
                  <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
                    {AUDITGUARD.name}
                  </h2>
                  <p className="mt-1 text-base font-medium text-aero">{AUDITGUARD.tagline}</p>
                </div>
              </div>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/75">{AUDITGUARD.intro}</p>

              <h3 className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-white/50">Platform components</h3>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {AUDITGUARD.components.map((c) => (
                  <li key={c.label} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/90">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-aero/15 text-aero">
                      <Icon name={c.icon} className="h-4 w-4" />
                    </span>
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur lg:p-8">
                <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50">{AUDITGUARD.servesTitle}</h3>
                <ul className="mt-4 space-y-3">
                  {AUDITGUARD.serves.map((s) => (
                    <li key={s} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/90">
                      <Icon name="Plane" className="h-4 w-4 text-aero" /> {s}
                    </li>
                  ))}
                </ul>
                <Link to="/login" data-testid="auditguard-launch" className="mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-aero px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
                  Launch AuditGuard AI <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" data-testid="auditguard-interest" className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                  Talk to our team
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const DeveloperShowcase = () => (
  <section data-testid="api-showcase" className="bg-background py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-border bg-navy text-white">
          <div className="grain relative grid gap-10 p-8 lg:grid-cols-12 lg:items-center lg:p-14">
            <div className="lg:col-span-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-aero">
                  <Code2 className="h-3.5 w-3.5" /> For Developers
                </span>
                <LiveBadgeDark />
              </div>
              <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                The AuditGuard API — aviation compliance, programmable.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/75">
                Bring AuditGuard's AI into your own systems. Generate audit reports, gap analyses,
                documents, corrective action plans, risk assessments, training programs and safety
                analyses from your dashboard, your tools or your own product.
              </p>
              <ul className="mt-7 space-y-3">
                {API_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm font-medium text-white/90">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-aero" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/login" data-testid="api-showcase-signup" className="inline-flex items-center gap-1.5 rounded-full bg-aero px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
                  Create a free account <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" data-testid="api-showcase-contact" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                  Discuss an integration
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div data-testid="api-code-card" className="overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-2xl">
                <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
                  <span className="h-3 w-3 rounded-full bg-red-400/70" />
                  <span className="h-3 w-3 rounded-full bg-amber-400/70" />
                  <span className="h-3 w-3 rounded-full bg-emerald-400/70" />
                  <span className="ml-3 text-xs text-white/40">risk-assessment.sh</span>
                </div>
                <pre className="overflow-x-auto p-5 text-xs leading-relaxed text-emerald-200/90">{API_CODE}</pre>
                <div className="flex flex-wrap gap-2 border-t border-white/10 px-5 py-4">
                  {API_ENDPOINTS.map((e) => (
                    <code key={e} className="rounded-md bg-white/5 px-2 py-1 text-[11px] text-aero ring-1 ring-white/10">{e}</code>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function AiSolutions() {
  return (
    <div data-testid="ai-solutions-page">
      <PageHeader
        testId="ai-solutions-header"
        eyebrow="AI Solutions · Live"
        title="An AI compliance ecosystem, purpose-built for aviation."
        subtitle="A working suite of AI-enabled solutions that pairs deep aviation expertise with modern automation. Create a free account and generate your first compliance report today."
        image={IMAGES.ai}
      />

      <Flagship />

      <section className="bg-secondary py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">The Ecosystem</span>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                Solution suites across every aviation discipline.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Each suite maps directly to our consulting service lines — so AI augments the experts,
                never replaces accountability. Every suite below is live inside AuditGuard AI today.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {AI_SOLUTIONS.map((sol, i) => (
              <Reveal key={sol.title} delay={(i % 3) * 0.06}>
                <div data-testid={`ai-solution-${i}`} className="flex h-full flex-col rounded-2xl border border-border bg-white p-8 transition-transform hover:-translate-y-1 hover:shadow-xl">
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-brand/5 ring-1 ring-brand/10">
                      {sol.img ? (
                        <img src={sol.img} alt="" aria-hidden="true" className="h-10 w-10 object-contain" />
                      ) : (
                        <Icon name={sol.icon} className="h-6 w-6 text-brand" />
                      )}
                    </span>
                    <LiveBadge />
                  </div>
                  <h3 className="mt-6 font-display text-lg font-bold text-navy">{sol.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{sol.summary}</p>
                  <ul className="mt-5 space-y-2.5 border-t border-border pt-5">
                    {sol.tools.map((t) => (
                      <li key={t} className="flex items-start gap-2.5 text-sm text-navy">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-12 rounded-2xl border border-emerald-300/60 bg-emerald-50 p-6 text-sm leading-relaxed text-steel lg:p-8">
              <strong className="font-display text-navy">Live today:</strong> the AuditGuard AI platform with all
              seven modules, the secure Client Portal with team workspaces, and the developer API that connects
              every tool to your own systems. <Link to="/login" className="font-semibold text-brand underline-offset-2 hover:underline">Create a free account</Link> to
              start, or <Link to="/contact" className="font-semibold text-brand underline-offset-2 hover:underline">talk to us</Link> about your operation.
            </div>
          </Reveal>
        </div>
      </section>

      <DeveloperShowcase />

      <CTASection />
    </div>
  );
}
